/**
 * Logo component that renders an SVG with customizable color
 *
 * @param {Object} props - Component properties
 * @param {string} props.currentColour - The color to apply to the SVG (defaults to "black")
 * @param {string} props.className - Additional CSS classes to apply
 * @param {number} props.size - Size of the logo in pixels (maintains aspect ratio)
 * @returns {JSX.Element} - Rendered logo component
 */
const Logo = ({ className = '', size = 100, animate = false, duration = 2 }) => {
  return (
    <>
      {animate && (
        <style>
          {`
              @keyframes drawLogoWithFade {
            /* Initial state: hidden */
            0% { 
                clip-path: inset(100% 0 0 0); 
                opacity: 0;
            }
            
            /* Animation to fully visible */
            85% { 
                clip-path: inset(0 0 0 0); 
                opacity: 1;
            }
            
            /* Final state: remain visible */
            100% { 
                clip-path: inset(0 0 0 0); 
                opacity: 1;
            }
            }

            @keyframes drawLogoLeftRight {
            /* Initial state: hidden */
            0% { 
                clip-path: inset(0 100% 0 0); 
                opacity: 0;
            }
            
            /* Animation to fully visible */
            85% { 
                clip-path: inset(0 0 0 0); 
                opacity: 1;
            }
            
            /* Final state: remain visible */
            100% { 
                clip-path: inset(0 0 0 0); 
                opacity: 1;
            }
            }

            /* Base animation class for top-to-bottom animation */
            .logo-animation {
            animation: drawLogoWithFade ${duration}s ease-in-out forwards;
            }

            /* Left-to-right animation class */
            .logo-animation-lr {
            animation: drawLogoLeftRight ${duration}s ease-in-out forwards;
            }
            
            `}
        </style>
      )}
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        width={size}
        height={size}
        viewBox="0 0 896 896"
      >
        <path
          className={animate ? 'logo-animation' : ''}
          fill="currentColor"
          d="
        M69.735535,530.993958 
            C69.772247,515.664124 68.925316,500.758911 70.166969,486.029755 
            C70.931686,476.958221 70.431519,467.721771 72.053093,458.882446 
            C73.850883,449.082520 75.281441,439.190460 77.120110,429.393616 
            C79.001778,419.367737 81.825569,409.600891 83.951775,399.660675 
            C85.922951,390.445282 89.308464,381.514160 92.332062,372.545837 
            C98.895363,353.078308 107.731903,334.590027 117.632309,316.653595 
            C123.333412,306.324951 129.414932,296.201782 136.158493,286.432129 
            C151.926758,263.587982 169.551422,242.347839 189.771957,223.414062 
            C203.306030,210.741241 217.483566,198.716873 232.519608,187.768570 
            C246.591904,177.522034 261.076752,167.914963 275.848785,158.711777 
            C294.341492,147.190552 313.862061,137.540771 333.628265,128.608673 
            C354.643860,119.111992 376.471832,111.505554 398.841919,105.478630 
            C414.368591,101.295425 429.854492,96.824562 445.697388,94.315277 
            C453.788239,93.033813 462.029938,91.586891 470.390961,91.750931 
            C482.051849,91.979713 493.729797,91.503204 505.384155,91.860008 
            C518.252808,92.253983 530.962402,93.694710 543.483032,97.329895 
            C551.188538,99.567108 559.151611,100.982071 566.719849,103.816399 
            C573.196777,106.242020 579.675232,108.667679 586.200500,110.958084 
            C588.967773,111.929413 590.425720,113.774437 591.298157,116.483437 
            C592.414307,119.949081 590.238464,123.101677 586.763550,122.281586 
            C578.275818,120.278473 569.929932,117.628433 561.395142,115.883675 
            C553.181946,114.204659 545.234802,111.117851 536.671265,111.155945 
            C518.175171,111.238243 499.655975,110.675476 481.191895,111.456299 
            C472.075531,111.841820 462.993958,114.110069 454.006836,116.404488 
            C443.600098,119.061325 433.255035,121.897224 422.992493,125.022423 
            C408.586884,129.409271 394.440735,134.474243 380.559113,140.410034 
            C363.015717,147.911606 345.817047,156.044174 328.910217,164.874939 
            C306.201874,176.735962 284.647766,190.488480 263.679565,205.087387 
            C250.653809,214.156433 238.387573,224.338135 226.324234,234.746948 
            C215.973938,243.677643 206.234665,253.212555 196.922806,263.115051 
            C182.669159,278.272736 169.536774,294.382202 157.808762,311.682159 
            C145.454681,329.905640 134.553116,348.913025 125.448608,368.870575 
            C119.641792,381.599396 114.966293,394.869690 110.643547,408.234100 
            C104.420479,427.473694 99.971031,447.118011 97.350822,467.116302 
            C94.254845,490.745972 93.448341,514.520508 94.785263,538.344238 
            C94.971825,541.668701 94.909370,545.007324 94.959045,548.339355 
            C95.182243,563.313049 98.221855,577.951477 100.105049,592.733093 
            C101.908905,606.892090 105.043053,620.760193 108.699005,634.479553 
            C112.239151,647.764404 115.927498,661.056702 120.958710,673.896912 
            C125.540565,685.590271 130.100235,697.295532 134.932144,708.886230 
            C137.416107,714.844666 140.220337,720.668457 142.759796,726.616333 
            C148.870514,740.928711 156.019348,754.805359 161.895294,769.206238 
            C163.867096,774.038757 168.047302,779.006348 164.806610,785.220520 
            C162.774734,789.116638 160.017471,790.256287 156.572662,787.435547 
            C154.268524,785.548828 151.976486,783.451904 150.304688,781.019104 
            C136.907089,761.523071 123.971397,741.722351 114.670761,719.856934 
            C106.845726,701.460693 99.188408,682.998413 93.388756,663.809448 
            C89.377869,650.538940 84.766029,637.394958 82.015129,623.832581 
            C79.538445,611.622131 76.832672,599.435120 75.343773,586.983276 
            C73.818756,574.229675 71.918152,561.579163 71.302147,548.692139 
            C71.032196,543.044556 69.759102,537.287476 69.735535,530.993958 
        z"
        />
        <path
          className={animate ? 'logo-animation-lr' : ''}
          fill="currentColor"
          opacity="1.000000"
          stroke="none"
          d="
        M311.350403,523.662476 
            C309.329773,517.085754 307.431641,510.876434 305.580109,504.653290 
            C302.055267,492.806152 302.790619,480.651367 303.836334,468.657532 
            C304.706055,458.682709 307.508789,448.969452 313.989838,440.902893 
            C315.466553,439.064941 317.572388,437.015503 320.400421,438.105011 
            C323.004089,439.108124 322.407654,441.717255 322.364258,443.784424 
            C322.246185,449.411530 321.154633,455.179626 321.379761,460.587280 
            C321.807312,470.856323 322.592010,481.345673 326.931213,491.091858 
            C333.080658,504.903992 339.512482,518.494263 348.123474,531.045898 
            C356.705139,543.554871 366.762665,554.765259 378.386719,564.223022 
            C387.949310,572.003479 398.095276,579.500916 409.738220,584.123047 
            C417.276154,587.115540 424.863770,589.936340 432.812744,592.091187 
            C446.376526,595.768005 459.965057,596.159729 473.622650,595.475464 
            C487.936493,594.758240 501.981384,592.260193 515.908997,588.164673 
            C528.836914,584.363098 541.291565,579.677063 553.224182,573.703613 
            C571.585266,564.512207 588.516235,553.132385 604.061951,539.552368 
            C620.845276,524.891174 633.957764,507.625305 642.772827,487.317688 
            C649.731506,471.286804 652.854126,454.296906 650.982910,436.731293 
            C649.833313,425.939484 650.150574,415.034851 647.833740,404.312927 
            C646.631042,398.746643 652.889587,392.088074 658.409302,392.249573 
            C660.305664,392.305054 661.436279,393.943146 662.393799,395.596191 
            C666.770691,403.152039 669.057312,411.558655 672.304016,419.574036 
            C676.129028,429.017181 677.822998,439.101166 679.814331,448.923157 
            C681.991577,459.662415 684.098022,470.958069 682.061462,481.935608 
            C680.144897,492.266449 677.894470,502.587311 673.428528,512.421204 
            C670.169617,519.597229 666.800171,526.556641 662.714905,533.301208 
            C659.396912,538.778931 655.572144,543.791382 651.597229,548.731506 
            C647.468872,553.862244 643.169800,558.874451 638.225464,563.241699 
            C621.308472,578.184082 603.514893,591.824280 583.370667,602.346741 
            C572.565063,607.991150 561.576416,613.212769 550.262817,617.549194 
            C538.591125,622.022766 526.573303,625.753601 514.273193,628.301758 
            C503.100708,630.616394 491.808289,631.930969 480.453766,633.157654 
            C473.664581,633.891113 466.809479,634.867859 460.229919,634.051086 
            C444.330750,632.077209 428.467163,630.317871 412.966766,625.064758 
            C399.166016,620.387573 386.640350,613.711853 375.024750,605.620544 
            C361.490509,596.192688 349.915527,584.347046 339.292084,571.495911 
            C329.409698,559.541321 321.435577,546.608459 314.718842,532.770386 
            C313.362244,529.975525 311.616730,527.288330 311.350403,523.662476 
        z"
        />

        <path
          className={animate ? 'logo-animation' : ''}
          fill="currentColor"
          opacity="1.000000"
          stroke="none"
          // middle figure
          d="
        M531.841003,333.694489 
            C540.788208,335.197174 549.423889,335.174225 557.939697,336.030579 
            C564.644531,336.704834 571.166809,340.094238 576.618896,344.537781 
            C579.278259,346.705231 579.323181,349.915863 576.442200,351.708038 
            C571.395996,354.847137 569.977661,358.957672 570.352295,364.685974 
            C570.818726,371.816742 570.173767,379.012756 570.398499,386.168304 
            C570.915161,402.622040 568.300171,418.890137 567.330322,435.245178 
            C566.870178,443.005676 567.002869,450.803925 566.293762,458.542542 
            C564.936401,473.357117 565.077087,488.300507 562.477539,503.007263 
            C562.419800,503.334045 562.363525,503.673706 562.389343,504.000824 
            C563.403503,516.840271 560.347839,529.477905 560.460205,542.244568 
            C560.513306,548.280884 559.570129,554.047363 558.268982,559.835571 
            C557.697571,562.377014 553.090576,563.616028 550.393677,561.542175 
            C544.405090,556.937073 540.074829,551.106506 537.411804,543.864746 
            C533.935486,534.411194 531.589844,524.619446 530.152771,514.799377 
            C529.250977,508.636749 527.228577,502.496368 527.683167,496.153442 
            C528.054260,490.975708 526.946228,485.916992 526.779175,480.785248 
            C526.700928,478.380341 525.523865,476.006714 523.361816,474.976257 
            C513.079529,470.075684 503.438110,464.118713 491.130920,463.953705 
            C483.600800,463.852753 476.474091,463.973206 469.361908,466.239258 
            C466.592102,467.121704 464.598419,469.122711 462.486359,470.969055 
            C458.065979,474.833252 453.122955,475.557098 447.783081,473.173523 
            C444.673645,471.785553 441.602539,472.175293 438.557129,473.175690 
            C436.323761,473.909302 434.117310,474.519226 431.731903,474.375519 
            C427.743683,474.135223 423.283539,475.435394 421.616089,469.689758 
            C418.885010,460.279144 419.737335,455.612793 425.286743,451.646698 
            C431.303528,447.346619 438.335175,445.528442 445.294250,443.893616 
            C448.674896,443.099457 450.481964,441.004822 451.255585,438.616791 
            C453.154877,432.753723 454.035706,426.569397 455.717194,420.624878 
            C457.705566,413.595337 458.788849,406.370758 460.897644,399.351776 
            C463.005280,392.336639 464.262878,385.048889 465.603516,377.826904 
            C466.419373,373.431824 464.510895,371.856354 459.897644,372.722656 
            C450.947571,374.403320 441.500549,370.184448 437.050781,362.555817 
            C431.957245,353.823456 433.654419,344.143707 442.704132,338.872559 
            C448.868683,335.281921 455.563568,334.468933 462.523895,333.369415 
            C479.752899,330.647705 496.985626,332.321869 514.195374,331.659515 
            C520.064941,331.433594 525.445679,334.855713 531.841003,333.694489 
        M531.673706,375.552673 
            C532.115540,371.526306 534.962341,367.809448 533.179382,363.337677 
            C521.737732,365.321991 510.111755,365.961670 498.625793,368.046875 
            C493.319763,369.010223 492.804626,370.933105 493.574799,375.200195 
            C495.652710,386.713165 492.566101,397.879578 491.140015,409.161591 
            C490.217804,416.457245 487.842743,423.445679 486.346619,431.128632 
            C500.844971,432.344360 514.360962,435.662567 526.392273,443.850983 
            C528.358948,440.933899 527.621338,438.221680 527.511658,435.678680 
            C527.413696,433.404724 526.140015,431.385681 526.211243,428.913147 
            C526.481934,419.516571 528.383850,410.210175 527.699158,400.762238 
            C527.593506,399.304779 527.798889,397.738373 528.240112,396.342407 
            C530.292603,389.848938 529.784302,382.930847 531.673706,375.552673 
        z"
        />
        <path
          className={animate ? 'logo-animation-lr' : ''}
          fill="currentColor"
          opacity="1.000000"
          stroke="none"
          // upper horizontal stroke
          d="
        M677.787598,263.215332 
            C668.217285,253.080505 658.230408,244.039001 647.127991,236.044327 
            C638.959045,230.161972 630.542358,224.812683 621.865234,219.830200 
            C611.649658,213.964325 600.756958,209.359406 589.539307,205.998383 
            C579.284485,202.925873 568.805237,200.266693 558.065918,199.116531 
            C544.505676,197.664276 530.933655,196.545425 517.285889,197.771835 
            C502.733978,199.079514 488.356781,201.460754 474.017090,204.271164 
            C453.946503,208.204788 434.228180,213.376221 415.013733,220.327454 
            C402.695312,224.783875 390.600739,229.824203 378.566956,235.057983 
            C361.780792,242.358688 345.929810,251.347229 330.191681,260.508759 
            C316.185333,268.662170 303.031097,278.184937 289.857849,287.683624 
            C277.356903,296.697510 266.321350,307.489807 253.605499,316.169281 
            C253.467972,316.263123 253.315552,316.337036 253.185715,316.439972 
            C249.579742,319.298492 245.164993,320.388336 241.654343,317.651764 
            C237.953003,314.766541 239.100098,309.956787 240.693344,306.135071 
            C247.312881,290.256531 258.169830,277.710358 271.167145,266.554901 
            C285.159027,254.545868 299.527893,243.151718 315.180420,233.397171 
            C330.082123,224.110535 345.429016,215.542328 361.471558,208.499329 
            C380.198029,200.278000 399.352966,193.114929 419.055115,187.308289 
            C434.284698,182.819809 449.656219,178.937881 465.195221,176.196579 
            C479.321777,173.704453 493.613678,171.547440 508.141266,171.554214 
            C516.609985,171.558151 525.078125,172.017120 533.547668,171.731964 
            C542.572266,171.428101 551.211121,174.210831 560.015381,175.132172 
            C570.169800,176.194839 579.566956,179.554031 589.249451,182.004547 
            C595.715210,183.640945 601.820496,186.657913 608.161499,188.841110 
            C618.805603,192.505859 628.217896,198.652283 637.958374,204.007584 
            C646.434631,208.667770 654.192749,214.765915 661.838745,220.775024 
            C674.881531,231.025543 686.932617,242.444946 697.931641,254.844330 
            C712.572083,271.348785 724.017517,289.951050 732.074463,310.538452 
            C739.636047,329.860016 739.538574,350.112915 737.959106,370.250153 
            C737.417053,377.159973 736.582153,384.604034 732.708618,391.042725 
            C731.186462,393.572937 729.413025,394.806824 726.645203,394.160767 
            C723.707703,393.475067 723.445435,390.986481 723.697388,388.581848 
            C724.768616,378.357910 726.199707,368.190430 725.712830,357.832489 
            C725.186157,346.628235 722.425781,335.979614 718.706116,325.527771 
            C713.629944,311.264374 705.928040,298.392181 697.165222,286.142242 
            C691.399048,278.081573 685.274902,270.293060 677.787598,263.215332 
        z"
        />
        <path
          className={animate ? 'logo-animation' : ''}
          fill="currentColor"
          opacity="1.000000"
          stroke="none"
          d="
        M384.375244,443.292389 
            C384.521057,450.570831 384.399200,457.330109 382.362030,463.951324 
            C381.299530,467.404633 377.753876,469.008087 374.087799,467.451080 
            C366.882507,464.390869 362.553040,458.551239 360.040039,451.629700 
            C355.748138,439.808319 352.540497,427.673126 353.650604,414.853455 
            C354.331238,406.993042 355.984924,399.277802 357.125031,391.502350 
            C358.664093,381.006409 362.789398,371.421295 366.005768,361.541321 
            C368.009674,355.385773 371.415863,349.611877 374.702240,343.963104 
            C383.680267,328.531433 394.828033,314.786865 407.530243,302.226532 
            C421.417175,288.494690 436.965485,276.955078 454.459381,268.520660 
            C466.501740,262.714630 479.245392,258.520325 492.931885,257.069031 
            C499.326752,256.390900 505.718201,255.439987 512.075195,255.817902 
            C526.103638,256.651855 539.384827,260.380005 552.111328,266.608582 
            C562.623657,271.753540 571.648987,278.874725 580.850952,285.837463 
            C583.838257,288.097870 583.464844,291.027374 582.365234,294.241028 
            C581.267822,297.448303 578.872498,296.841431 576.644714,296.613678 
            C570.861023,296.022369 565.605042,293.460175 560.115356,291.811646 
            C540.924805,286.048981 522.131714,283.602448 502.153076,289.871674 
            C481.927734,296.218292 465.083252,307.409821 448.750275,320.134491 
            C441.147797,326.057465 434.233582,332.712189 428.063995,339.956085 
            C421.524567,347.634216 415.340149,355.643951 410.136200,364.467590 
            C405.807159,371.807739 401.790070,379.259979 398.326202,386.980286 
            C394.669312,395.130859 392.357147,403.785004 389.875214,412.391174 
            C386.987305,422.405090 386.728119,432.782440 384.375244,443.292389 
        z"
        />
      </svg>
    </>
  );
};

export default Logo;
